import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import { useI18n } from "@/lib/I18nProvider";
import { BLOG_POSTS, getPostBySlug } from "@/lib/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (!post) navigate("/blog", { replace: true });
  }, [post, navigate]);

  if (!post) return null;

  const minRead = lang === "fr" ? "min de lecture" : lang === "es" ? "min de lectura" : lang === "pt" ? "min de leitura" : "min read";
  const backLabel = lang === "fr" ? "Tous les articles" : lang === "es" ? "Todos los artículos" : lang === "pt" ? "Todos os artigos" : "All articles";
  const moreLabel = lang === "fr" ? "Lire aussi" : lang === "es" ? "Leer también" : lang === "pt" ? "Ler também" : "Read next";

  // Suggest 2 other posts
  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div data-testid="blog-post-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8" data-testid="blog-post-back">
            <ArrowLeft size={16} /> {backLabel}
          </Link>

          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span key={tag} className="overline !text-[#1FA8FF] !border-[#1FA8FF]/30 !bg-[#1FA8FF]/10">{tag}</span>
            ))}
          </div>

          <h1 className="font-display mt-6 text-3xl sm:text-4xl md:text-6xl font-bold tracking-tighter leading-[1.05] text-white">
            {post.title}
          </h1>

          <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-slate-300">
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest">
              <Calendar size={14} className="text-[#1FE0A8]" />
              {new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest">
              <Clock size={14} className="text-[#1FE0A8]" />
              {post.read_time} {minRead}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">
              {post.author}
            </span>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-8 mb-12">
        <div className="rounded-3xl overflow-hidden glass">
          <div className="aspect-[16/9] overflow-hidden">
            <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        {post.content.map((block, i) => {
          if (block.type === "h2") {
            return (
              <motion.h2
                key={i}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
                className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mt-12 mb-4"
              >
                {block.text}
              </motion.h2>
            );
          }
          return (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
              className="text-base md:text-lg text-slate-200 leading-relaxed mb-5"
            >
              {block.text}
            </motion.p>
          );
        })}

        {/* Inline CTA */}
        <div className="mt-12 p-7 glass rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#1FE0A8]">{lang === "fr" ? "Envie de lancer ?" : lang === "es" ? "¿Listo para lanzar?" : lang === "pt" ? "Pronto para lançar?" : "Ready to launch?"}</div>
            <div className="font-display text-xl font-bold mt-1 text-white">{lang === "fr" ? "Réservez une démo de 30 minutes." : lang === "es" ? "Reserva una demo de 30 minutos." : lang === "pt" ? "Marque uma demo de 30 minutos." : "Book a 30-minute demo."}</div>
          </div>
          <Link to="/demo" className="btn-primary shrink-0" data-testid="blog-post-cta-demo">
            {t("cta.demo_short")} <ArrowRight size={18} />
          </Link>
        </div>
      </article>

      {/* Read next */}
      <section className="py-16 md:py-20 border-t border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mb-8">{moreLabel}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="card-hover glass rounded-2xl overflow-hidden flex group" data-testid={`blog-next-${p.slug}`}>
                <div className="w-1/3 shrink-0 bg-[#1A2236] overflow-hidden">
                  <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 md:p-6 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#1FE0A8]">{p.tags[0]}</div>
                  <h4 className="font-display mt-2 text-lg md:text-xl font-bold tracking-tight text-white group-hover:text-[#1FA8FF] transition-colors leading-tight">{p.title}</h4>
                  <div className="mt-3 text-xs text-slate-400 font-mono uppercase tracking-widest">{p.read_time} {minRead}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
